import { type ClipboardEvent, useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import { EditorContent } from '@tiptap/react';
import { color } from '@/colors';
import { useCaptureElementWheelScroll } from '@/hooks/useCaptureElementWheelScroll';
import { useAnalytics } from '@/hooks/useAnalytics';
import { PROMPT_VARIABLE_EVENT_NAMES } from '@/consts/analytics.consts';
import { HandleType } from '@/enums/handle-type.enum';
import { PROMPT_NODE_ID } from '@/consts/node-types.consts';
import { useNodeNullable } from '@/components/Recipe/FlowContext';
import { getHandleId } from '../Utils';
import { useTiptapEditor } from './hooks/useTiptapEditor';
import { PromptEditorContextMenu } from './PromptEditorContextMenu';
import { PromptNodeFooter } from './PromptNodeFooter';
import {
  CSS_CLASSES,
  EDITOR_DATA_ATTR,
  MENTION_TAG_NAME,
  TAG_BACKGROUND_COLOR,
  TAG_BACKGROUND_COLOR_HOVER,
  TAG_BACKGROUND_COLOR_ACTIVE,
  SOURCE_COLOR,
  VALUE_COLOR,
  EMPTY_VALUE_COLOR,
} from './constants';
import type { AvailableInput } from './types';
import type { NodeId, PromptDisplayMode } from 'web';
import type { AddNewNodeOptions } from '@/components/Recipe/flowTypes';

interface PromptEditorProps {
  nodeId: NodeId;
  availableInputs: AvailableInput[];
  value: string;
  placeholder: string;
  maxLength: number;
  onChange: (prompt: string) => void;
  onPaste: (evt: ClipboardEvent<HTMLDivElement>) => void;
  hasEditingPermissions: boolean;
  displayMode: PromptDisplayMode;
  addNewNode: (options: AddNewNodeOptions) => void;
  addInputHandle: () => AvailableInput;
  hasFocus: boolean;
  setHasFocus: (focused: boolean) => void;
}

export function PromptEditor({
  nodeId,
  availableInputs,
  value,
  placeholder,
  maxLength,
  onChange,
  onPaste,
  hasEditingPermissions,
  displayMode,
  addNewNode,
  addInputHandle,
  hasFocus,
  setHasFocus,
}: PromptEditorProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const currentNode = useNodeNullable(nodeId);
  const { track } = useAnalytics();

  const handleCreatePrompt = useCallback(
    (queryText: string) => {
      if (!currentNode) return undefined;

      // Create input handle for this prompt node and get the key
      const inputHandle = addInputHandle();

      // Construct the full handle ID in the format "nodeId-input-key"
      const targetHandleId = getHandleId(nodeId, 'input', inputHandle.key);

      const position = {
        x: currentNode.position.x - 650,
        y: currentNode.position.y,
      };

      // Create new prompt node with the queryText and connection to the new input handle
      addNewNode({
        action: {
          id: PROMPT_NODE_ID,
          initialData: {
            prompt: queryText,
          },
        },
        dropX: position.x,
        dropY: position.y,
        connection: {
          nodeId,
          handleId: targetHandleId,
          handleSide: 'target',
          handleType: HandleType.Text,
        },
      });

      track(PROMPT_VARIABLE_EVENT_NAMES.CREATE_VARIABLE, { text: queryText });

      return inputHandle;
    },
    [addInputHandle, addNewNode, nodeId, currentNode, track],
  );

  const { editor, contextMenu, setContextMenu } = useTiptapEditor({
    value,
    availableInputs,
    hasEditingPermissions,
    placeholder,
    onChange,
    onPaste,
    displayMode,
    maxLength,
    setHasFocus,
    onCreatePrompt: handleCreatePrompt,
  });

  const isMentionMenuOpen = contextMenu.type === 'mention';
  const hasInputHandles = availableInputs.length > 0;

  useCaptureElementWheelScroll(hasFocus ? scrollContainerRef : { current: null });

  // Create a single-line drag image clone to avoid multi-line visual artifacts
  const handleDragStart = useCallback((event: React.DragEvent) => {
    const target = event.target as HTMLElement;
    const tagElement = target.closest(`[${EDITOR_DATA_ATTR.TAG_KEY}]`) as HTMLElement;

    if (tagElement) {
      // Add dragging class to the tag being dragged
      tagElement.classList.add(CSS_CLASSES.TAG_DRAGGING);

      // Clone the tag inside the same container so it inherits all MUI sx styles automatically
      const clone = tagElement.cloneNode(true) as HTMLElement;
      clone.style.display = 'inline-block';
      clone.style.maxWidth = '300px';
      clone.style.overflow = 'hidden';
      clone.style.textOverflow = 'ellipsis';
      clone.style.whiteSpace = 'nowrap';
      // Set color to VALUE_COLOR so the ellipsis "..." matches the value text.
      clone.style.color = VALUE_COLOR;

      scrollContainerRef.current?.parentElement?.appendChild(clone);
      event.dataTransfer.setDragImage(clone, 0, 0);

      // The browser captures the drag image synchronously, so we can remove on the next frame
      requestAnimationFrame(() => clone.remove());
    }
  }, []);

  const handleDragEnd = useCallback((event: React.DragEvent) => {
    // Remove dragging class from the tag
    const target = event.target as HTMLElement;
    const tagElement = target.closest(`[${EDITOR_DATA_ATTR.TAG_KEY}]`);
    if (tagElement) {
      tagElement.classList.remove(CSS_CLASSES.TAG_DRAGGING);
      // Clear selection after drop to prevent tag from appearing selected
      tagElement.classList.remove(CSS_CLASSES.TAG_SELECTED);
    }
  }, []);

  const handleDrop = useCallback(() => {
    if (editor) {
      editor.commands.setTextSelection(editor.state.selection.to);
    }
  }, [editor]);

  const handleScrollContainerMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      // Only focus if clicking on the container itself (not on editor content)
      if (editor && event.target === event.currentTarget) {
        // Prevent the default mousedown so the browser doesn't steal focus/selection
        event.preventDefault();
        if (!editor.isFocused) {
          editor.commands.focus('end');
        }
      }
    },
    [editor],
  );

  return (
    <>
      <Box
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDrop={handleDrop}
        sx={{
          width: '100%',
          position: 'relative',
          [`& .${CSS_CLASSES.EDITOR}`]: {
            width: '100%',
            fontSize: 16,
            fontWeight: 400,
            color: color.White100,
            lineHeight: 1.5,
            cursor: !hasEditingPermissions ? 'default' : 'text',
            pointerEvents: !hasEditingPermissions ? 'none' : 'auto',
            outline: 'none',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            // Hide real browser caret when mention menu is open (fake caret is shown inside the tag)
            ...(isMentionMenuOpen && { caretColor: 'transparent' }),
            '&:focus': {
              outline: 'none',
            },
            '& p': {
              margin: 0,
            },
            '&.ProseMirror-focused': {
              outline: 'none',
            },
            // Placeholder styling
            '& p.is-editor-empty:first-of-type::before': {
              content: 'attr(data-placeholder)',
              pointerEvents: 'none',
              color: color.White40_T,
              float: 'left',
              height: 0,
            },
          },
          // Tag styling
          [`& [${EDITOR_DATA_ATTR.TAG_KEY}]`]: {
            display: 'inline',
            verticalAlign: 'baseline',
            margin: '0 2px',
            cursor: hasEditingPermissions ? 'grab' : 'default',
            userSelect: 'none',
            backgroundColor: TAG_BACKGROUND_COLOR,
            color: SOURCE_COLOR,
            padding: '0 4px',
            borderRadius: 1,
            transition: 'opacity 0.15s ease-in-out',
            boxDecorationBreak: 'clone',
            WebkitBoxDecorationBreak: 'clone',
            '&:hover': {
              backgroundColor: TAG_BACKGROUND_COLOR_HOVER,
            },
            '&:active': {
              cursor: 'grabbing',
            },
            [`&.${CSS_CLASSES.TAG_DRAGGING}`]: {
              opacity: 0.3,
            },
            [`&.${CSS_CLASSES.TAG_CONTEXT_MENU_OPEN}, &:active, &.${CSS_CLASSES.TAG_SELECTED}`]: {
              backgroundColor: TAG_BACKGROUND_COLOR_ACTIVE,
              boxShadow: `inset 0 0 0 1px ${color.White16_T}`,
            },
            // Add invisible space after tag so cursor appears outside when tag is last element
            '&::after': {
              content: '"\\200B"', // Zero-width space
              display: 'inline',
            },
            // Label part styling (includes label and separator)
            [`& .${CSS_CLASSES.TAG_LABEL}`]: {
              color: SOURCE_COLOR,
            },
            // Value part styling
            [`& .${CSS_CLASSES.TAG_VALUE}`]: {
              color: VALUE_COLOR,
            },
            // Empty value styling (when not connected or empty value)
            [`& .${CSS_CLASSES.TAG_VALUE_EMPTY}`]: {
              color: EMPTY_VALUE_COLOR,
            },
          },
          // Mention trigger tag: show a blinking caret inside the tag
          [`& [${EDITOR_DATA_ATTR.TAG_KEY}="${MENTION_TAG_NAME}"]`]: {
            '&::after': {
              display: 'inline-block',
              width: '1px',
              height: 'calc(1em + 1px)',
              backgroundColor: color.White100,
              verticalAlign: 'sub',
              marginLeft: '1px',
              animation: 'caretBlink 1s step-end infinite',
            },
          },
        }}
        className={hasFocus ? 'nodrag' : ''}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <Box
          ref={scrollContainerRef}
          onMouseDown={handleScrollContainerMouseDown}
          sx={{
            backgroundColor: color.Black84,
            border: `1px solid ${hasFocus ? color.White16_T : color.White04_T}`,
            borderRadius: 2,
            p: 3,
            minHeight: '215px',
            maxHeight: '528px',
            overflowY: 'auto',
            transition: 'border-color 0.2s ease-in-out',
            scrollbarColor: `${color.Black92} transparent`,
            cursor: !hasEditingPermissions ? 'default' : 'text',
          }}
        >
          <EditorContent editor={editor} />
        </Box>
        <PromptEditorContextMenu
          editor={editor}
          availableInputs={availableInputs}
          displayMode={displayMode}
          contextMenu={contextMenu}
          setContextMenu={setContextMenu}
          addInputHandle={addInputHandle}
          onCreatePrompt={handleCreatePrompt}
        />
      </Box>
      <PromptNodeFooter
        addInputHandle={addInputHandle}
        displayMode={displayMode}
        nodeId={nodeId}
        hasInputHandles={hasInputHandles}
        editor={editor}
        editorScrollContainerRef={scrollContainerRef}
      />
    </>
  );
}
